import React, { useState } from 'react';
import { Filter, ChevronDown, X, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PanelsList from '../../features/panels/components/PanelsList';
import ContributorsList from '../../features/contributors/components/ContributorsList';
import ShareCaseWizard from '../../features/cases/components/ShareCaseWizard';
import PostOpportunityModal from '../../features/opportunities/components/PostOpportunityModal';
import { useFilters } from '../../context/FilterContext';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const { species, toggleSpecies, caseType, setCaseType } = useFilters();

  const speciesOptions = ['All', 'Canine', 'Feline', 'Equine', 'Avian', 'Exotic'];
  const caseTypeOptions = ['All'];

  const { user } = useAuth();
  const role = user?.role || 'student';

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden animate-fadeIn"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full z-50 w-[280px] bg-white border-r border-gray-200 
          transform transition-transform duration-300 ease-in-out overflow-y-auto scrollbar-hide
          lg:static lg:translate-x-0 lg:z-auto lg:w-[260px] lg:flex-shrink-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Mobile close button */}
        <div className="flex items-center justify-between p-4 lg:hidden border-b border-gray-100">
          <span className="text-sm font-semibold text-text-black">Menu</span>
          <button
            id="close-sidebar"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            <X size={18} className="text-light-gray" />
          </button>
        </div>

        {/* Mobile-only action buttons */}
        <div className="lg:hidden px-4 pt-3 pb-1 space-y-2">
          {role !== 'student' && (
            <button 
              onClick={() => { onClose(); setShowShareModal(true); }}
              className="w-full py-2 text-sm font-medium text-accent-indigo border border-accent-indigo 
                               rounded-lg hover:bg-accent-indigo hover:text-white transition-all duration-200"
            >
              Share a Case
            </button>
          )}
          {role !== 'student' && (
            <button 
              onClick={() => { onClose(); setShowPostModal(true); }}
              className="w-full py-2 text-sm font-medium text-text-black border border-gray-200 
                               rounded-lg hover:bg-gray-50 transition-all duration-200"
            >
              Hire Vet
            </button>
          )}
        </div>

        <div className="p-4">
          {/* Home Link */}
          <button
            onClick={() => navigate(role === 'student' ? '/dashboard' : '/feed')}
            className="flex items-center gap-3 w-full px-3 py-2.5 text-sm font-medium text-white bg-accent-indigo
                       rounded-lg transition-colors duration-200 mb-4"
          >
            <Home size={18} />
            <span>Home</span>
          </button>

          {/* Filters Toggle */}
          <button
            id="filters-toggle"
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-text-black
                       hover:bg-gray-50 rounded-lg transition-colors duration-200 mb-4"
          >
            <div className="flex items-center gap-2">
              <Filter size={15} className="text-light-gray" />
              <span>Filters</span>
            </div>
            <ChevronDown 
              size={16}
              className={`text-light-gray transition-transform duration-200 ${filtersOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Filter options (collapsible) */}
          {filtersOpen && (
            <div className="mb-4 px-1 py-1 space-y-4 text-sm animate-fadeIn">
              {/* Species Section */}
              <div>
                <span className="text-xs font-semibold text-light-gray uppercase tracking-wider block mb-2 px-2">
                  Species
                </span>
                <div className="space-y-1">
                  {speciesOptions.map((opt) => {
                    const isActive = species.includes(opt);
                    return (
                      <button
                        key={opt}
                        onClick={() => toggleSpecies(opt)}
                        className={`w-full text-left px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                          isActive
                            ? 'bg-accent-indigo text-white shadow-sm'
                            : 'text-text-black hover:bg-gray-100'
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Case Type Section */}
              <div>
                <span className="text-xs font-semibold text-light-gray uppercase tracking-wider block mb-2 px-2">
                  Case Type
                </span>
                <div className="space-y-1">
                  {caseTypeOptions.map((opt) => {
                    const isActive = caseType === opt;
                    return (
                      <button
                        key={opt}
                        onClick={() => setCaseType(opt)}
                        className={`w-full text-left px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                          isActive
                            ? 'bg-accent-indigo text-white shadow-sm'
                            : 'text-text-black hover:bg-gray-100'
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Panels */}
          <PanelsList />

          {/* Divider */}
          <div className="border-t border-gray-100 my-4" />

          {/* Top Contributors */}
          <ContributorsList />
        </div>
      </aside>

      <ShareCaseWizard
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
      <PostOpportunityModal
        isOpen={showPostModal}
        onClose={() => setShowPostModal(false)}
      />
    </>
  );
};

export default Sidebar;